import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeroEntity, ItemEntity } from '@generated/gql';
import { ItemEntityExtendedType } from '@models/item-entity-extended.type';
import { RadioGroupModel } from '@models/radio-group.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppStateService } from '@services/app-state-service/app-state.service';
import { DestroyService } from '@services/destroy.service';
import { SubgraphService } from '@services/graph/subgraph.service';
import { Mediator } from '@services/mediator.service';
import { HeroControllerService } from '@services/onchain/hero-controller.service';
import { ItemBoxControllerService } from '@services/onchain/item-box-controller.service';
import { ItemControllerService } from '@services/onchain/item-controller.service';
import { TokenService } from '@services/onchain/token.service';
import { ProviderService } from '@services/provider.service';
import { SortItemsService } from '@services/sort-items.service';
import { InventoryActions } from '@shared/actions/inventory.actions';
import { LoadingActions } from '@shared/actions/loading.actions';
import { CHECKBOX_STATE } from '@shared/constants/checkbox-states.constant';
import { ItemActionType } from '@shared/constants/inventory.constants';
import {
  ITEM_SLOT,
  ITEM_TYPE,
  ITEM_TYPE_NAMES,
  ITEM_TYPE_TO_ITEM_SLOTS_MAPPING,
} from '@shared/constants/items.constant';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';
import { ITEMS_SORT_OPTIONS } from '@shared/constants/sort.constant';
import { keysOfEnum, rangeArray } from '@shared/utils';
import { OTHER_ITEMS, prepareDataForOtherItems } from '@shared/utils/items-utils';
import { NGXLogger } from 'ngx-logger';
import { debounceTime, distinctUntilChanged, filter, finalize, forkJoin, takeUntil } from 'rxjs';

import { CheckboxComponent } from '../../checkbox/checkbox.component';
import { DialogTitleComponent } from '../../dialog-title/dialog-title.component';
import { DROPDOWN_SIZE } from '../../dropdown/constants/dropdown-sizes.constant';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { DropdownItemModel } from '../../dropdown/model/dropdown-item.model';
import { IFilter } from '../../filters-dialog/interfaces/filters.interface';
import { ItemsFiltersDialogComponent } from '../../filters-dialog/items-filter/items-filters-dialog.component';
import { LoadingSmallComponent } from '../../loading-small/loading-small.component';
import { RadioGroupComponent } from '../../radio-group/radio-group.component';
import { SearchComponent } from '../../search/search.component';
import { InventoryAllComponent } from '../inventory-all/inventory-all.component';
import { InventoryEquippedComponent } from '../inventory-equipped/inventory-equipped.component';

import { INVENTORY_VIEW } from './inventory-view';

export enum VIEW_MODE {
  ICON,
  LIST,
}

@Component({
  standalone: true,
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  host: {
    class: 'app-window-responsive-background g-flex-column',
  },
  imports: [
    LoadingSmallComponent,
    DialogTitleComponent,
    RadioGroupComponent,
    DropdownComponent,
    SearchComponent,
    TranslatePipe,
    InventoryEquippedComponent,
    InventoryAllComponent,
    CheckboxComponent,
    ReactiveFormsModule,
  ],
})
export class InventoryComponent implements OnInit {
  // common
  account: string;
  chainId: number;
  loading = true;

  // hero
  heroAdr: string;
  heroId: number;
  hero: HeroEntity;

  items: ItemEntityExtendedType[] = [];
  availableItemsBySlots = new Map<ITEM_SLOT, ItemEntity[]>();
  equippedItemBySlots = new Map<ITEM_SLOT, ItemEntity>();

  equipWaiting = false;
  useWaiting = false;
  INVENTORY_VIEW = INVENTORY_VIEW;
  currentInventoryView = INVENTORY_VIEW.EQUIPPED;

  currentFilterItemClass = -1;

  itemSlotsArray = keysOfEnum(ITEM_SLOT);
  DROPDOWN_SIZE = DROPDOWN_SIZE;

  inventoryTypes: RadioGroupModel[] = [
    {
      id: INVENTORY_VIEW.ALL,
      label: 'inventory.types.all',
    },
    {
      id: INVENTORY_VIEW.EQUIPPED,
      label: 'inventory.types.equipped',
    },
  ];

  itemsTypeNamesOptions: DropdownItemModel[] = [...ITEM_TYPE_NAMES].map(([value, name]) => {
    return new DropdownItemModel({
      label: this.translateService.instant(name),
      id: value.toString(),
    });
  });
  dropdownTypeNamesSelected: DropdownItemModel;

  searchControl: FormControl<string | null> = new FormControl<string | null>(null);
  currentFilterQuery: { query: string | null } = { query: null };

  sortDirection = -1;
  itemsSortOptions: DropdownItemModel[] = ITEMS_SORT_OPTIONS;
  dropdownSortSelected = this.itemsSortOptions[0];
  viewMode: { query: VIEW_MODE } = { query: VIEW_MODE.ICON };
  currentItemsSort: { query: string };
  filters?: IFilter[] = [];
  generalFilters?: { [key: string]: { min: string; max: string } };
  onlyConcreteItemType = -1;
  onlyConcreteHeroes = 0;
  filteredItems: ItemEntityExtendedType[] = [];

  hasSandboxItems = false;
  suitableItemsControl: FormControl = new FormControl(CHECKBOX_STATE.NONE);
  isSuitableItems = false;

  private isNeedToUpdateHero = false;
  private heroToken: string;
  private heroTokenId: number;
  private filtersDialogRef:
    | DialogRef<
        { filters: IFilter[]; general: { [key: string]: { min: string; max: string } } },
        ItemsFiltersDialogComponent
      >
    | undefined;

  constructor(
    @Inject(DIALOG_DATA) public data: { heroToken: string; heroId: number; currentTab: INVENTORY_VIEW },
    private destroy$: DestroyService,
    private tokenService: TokenService,
    private itemControllerService: ItemControllerService,
    private heroControllerService: HeroControllerService,
    private changeDetectorRef: ChangeDetectorRef,
    private providerService: ProviderService,
    private appStateService: AppStateService,
    private subgraphService: SubgraphService,
    private itemBoxControllerService: ItemBoxControllerService,
    private router: Router,
    private translateService: TranslateService,
    private dialog: Dialog,
    private logger: NGXLogger,
    private mediator: Mediator,
    private dialogRef: DialogRef<boolean, InventoryComponent>,
    private sortItemsService: SortItemsService,
  ) {
    this.heroAdr = this.data.heroToken;
    this.heroId = this.data.heroId;
    this.currentInventoryView = this.data.currentTab ? this.data.currentTab : INVENTORY_VIEW.EQUIPPED;

    this.appStateService.setHeaderState({
      isInventoryOpen: true,
      isAccountBalanceShown: true,
      backUrl: [MAIN_ROUTES.MAIN, MAIN_ROUTES.HERO_CAMP],
    });

    this.resetInventorySlots();
  }

  ngOnInit(): void {
    this.heroAdr = this.data.heroToken;
    this.heroId = this.data.heroId;

    this.itemsTypeNamesOptions = [
      new DropdownItemModel({
        label: 'All',
        id: '-1',
      }),
      ...this.itemsTypeNamesOptions,
    ];

    this.dropdownTypeNamesSelected = this.itemsTypeNamesOptions[0];

    this.providerService.subscribeOnAccountAndNetwork(
      this.destroy$,
      this.changeDetectorRef,
      account => {
        this.account = account;
        this.init();
      },
      chainId => {
        this.chainId = chainId;
        this.heroControllerService.adjustFees();
        this.itemControllerService.adjustFees();
        this.tokenService.adjustFees();
        this.init();
      },
    );

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(query => {
        this.currentFilterQuery = { query };

        this.getPositionsPerPageWithSortAndFilter();

        this.changeDetectorRef.detectChanges();
      });

    this.suitableItemsControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.isSuitableItems = value === CHECKBOX_STATE.CHECKED;
      this.getPositionsPerPageWithSortAndFilter();

      this.changeDetectorRef.detectChanges();
    });

    this.updateTabParam(this.currentInventoryView);

    this.mediator.ofAction(InventoryActions.Update, this.destroy$).subscribe(() => {
      this.init();
    });
  }

  onHeroSelected(heroId: string | undefined): void {
    if (heroId === this.heroAdr + '_' + this.heroId) {
      return;
    }
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([MAIN_ROUTES.MAIN, MAIN_ROUTES.INVENTORY, heroId?.split('_')[0], heroId?.split('_')[1]]);
  }

  onSelectInventory({ id }) {
    this.updateTabParam(id);

    this.currentInventoryView = id;
  }

  setItemSorting(query: string): void {
    if (this.currentItemsSort && this.currentItemsSort.query === query) {
      this.sortDirection = -1 * this.sortDirection;
    }

    this.currentItemsSort = { query };

    this.changeDetectorRef.detectChanges();
  }

  isItemEligibleForHero(item: ItemEntity) {
    return (
      item.meta.requirements.strength <= this.hero.core.strength &&
      item.meta.requirements.dexterity <= this.hero.core.dexterity &&
      item.meta.requirements.energy <= this.hero.core.energy &&
      item.meta.requirements.vitality <= this.hero.core.vitality &&
      item.durability > 0
    );
  }

  switchViewTo(view: INVENTORY_VIEW): void {
    this.currentInventoryView = view;
  }

  itemAction(data: ItemActionType) {
    if (data?.useItem) {
      this.use(data.useItem);
    } else if (data?.useOtherItem) {
      this.useOtherItem(data.useOtherItem);
    }
  }

  onChangeViewMode() {
    this.viewMode = { query: this.viewMode.query === VIEW_MODE.LIST ? VIEW_MODE.ICON : VIEW_MODE.LIST };
  }

  openFilter() {
    this.filtersDialogRef = this.dialog.open(ItemsFiltersDialogComponent, {
      panelClass: 'app-overlay-pane',
      data: {
        filters: this.filters,
        general: this.generalFilters,
        isSell: false,
        isPriceDisabled: true,
      },
    });

    this.filtersDialogRef.closed
      .pipe(
        filter(data => !!data),
        takeUntil(this.destroy$),
      )
      .subscribe(data => {
        this.filters = data!.filters;
        this.generalFilters = data!.general;

        this.onlyConcreteItemType = -1;
        this.onlyConcreteHeroes = 0;
        this.getPositionsPerPageWithSortAndFilter();
      });
  }

  private getPositionsPerPageWithSortAndFilter() {
    this.filteredItems = [];
    this.filteredItems = this.sortItemsService.sortItems(
      this.items,
      this.currentFilterQuery?.query,
      this.filters,
      this.generalFilters,
      this.onlyConcreteItemType,
      true,
      this.sortDirection,
      this.currentItemsSort?.query,
    );

    this.changeDetectorRef.detectChanges();
  }

  use(item: ItemEntity) {
    this.logger.trace('use item', item);
    this.useWaiting = true;

    this.changeDetectorRef.markForCheck();
    this.itemControllerService
      .use$(this.account, this.chainId, item.meta.id, item.itemId, this.hero.meta.id, this.hero.heroId)
      .pipe(
        finalize(() => {
          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.useWaiting = false;
        // refresh inventory
        this.loadHeroInfo();
        this.changeDetectorRef.markForCheck();
      });
  }

  useOtherItem(item: ItemEntity) {
    this.logger.trace('use other item', item);
    const data = prepareDataForOtherItems(item.meta, this.hero.meta.id, this.hero.heroId);

    if (data) {
      this.useWaiting = true;
      this.changeDetectorRef.markForCheck();

      this.itemControllerService
        .useOtherItem$(this.account, this.chainId, item.meta.id, item.itemId, data)
        .pipe(
          finalize(() => {
            this.useWaiting = false;
            this.changeDetectorRef.markForCheck();
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          if (item.meta.name === OTHER_ITEMS.exitFromDungeon) {
            // this.router.navigate([MAIN_ROUTES.HERO, this.hero.meta.id, this.hero.heroId]);
            this.router.navigate([MAIN_ROUTES.MAIN, MAIN_ROUTES.HERO_CAMP]);
          }
          // refresh inventory
          this.loadHeroInfo();
        });
    }
  }

  equip(data: { item: ItemEntity; slot: number }) {
    if (data) {
      if (data.item.meta.itemType === ITEM_TYPE.TWO_HAND) {
        data.slot = ITEM_SLOT.TWO_HAND;
      }

      this.equipWaiting = true;

      this.changeDetectorRef.markForCheck();

      this.itemControllerService
        .equip$(
          this.account,
          this.chainId,
          [data.item.meta.id],
          [data.item.itemId],
          this.hero.meta.id,
          this.hero.heroId,
          [data.slot],
        )
        .pipe(
          finalize(() => {}),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.equipWaiting = false;
          this.isNeedToUpdateHero = true;
          // refresh inventory
          this.loadHeroInfo();
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  takeOff(data: { item?: ItemEntity; slot?: number } | undefined) {
    if (data && data.item) {
      if (data.item.meta.itemType === ITEM_TYPE.TWO_HAND) {
        data.slot = ITEM_SLOT.TWO_HAND;
      }

      const item = data.item;
      this.equipWaiting = true;

      this.changeDetectorRef.markForCheck();

      this.itemControllerService
        .takeOff$(
          this.account,
          this.chainId,
          [item ? item.meta.id : ''],
          [item ? item.itemId : 0],
          this.hero.meta.id,
          this.hero.heroId,
          [data && data.slot ? data.slot : 0],
        )
        .pipe(
          finalize(() => {}),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.equipWaiting = false;
          this.isNeedToUpdateHero = true;
          // refresh inventory
          this.loadHeroInfo();
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  takeOffAllEmit() {
    this.equipWaiting = true;
    const itemIds: number[] = [];
    const itemAddresses: string[] = [];
    const itemSlots: number[] = [];

    this.hero.items.forEach(item => {
      itemIds.push(item.itemId);
      itemAddresses.push(item.meta.id);
      itemSlots.push(item.equippedSlot ?? 0);
    });

    if (itemIds.length === 0) {
      this.logger.trace('No items to take off all');
      return;
    }

    this.changeDetectorRef.markForCheck();
    this.itemControllerService
      .takeOff$(this.account, this.chainId, itemAddresses, itemIds, this.hero.meta.id, this.hero.heroId, itemSlots)
      .pipe(
        finalize(() => {}),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.equipWaiting = false;
        this.isNeedToUpdateHero = true;
        // refresh inventory
        this.loadHeroInfo();
        this.changeDetectorRef.markForCheck();
      });
  }

  withdrawAllItemFromSandbox(): void {
    this.loading = true;
    const items = this.items.filter(item => item.itemSandBoxMode === 2);
    if (items.length > 0) {
      this.itemBoxControllerService
        .withdrawActiveItems$(
          this.account,
          this.chainId,
          this.hero.meta.id,
          this.hero.heroId,
          items.map(it => it.meta.id),
          items.map(it => it.itemId),
        )
        .pipe(
          finalize(() => (this.loading = false)),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.init();
        });
    } else {
      this.loading = false;
    }
  }

  setInventoryItemTypeFilter({ id }): void {
    this.currentFilterItemClass = Number(id);
  }

  close(): void {
    this.dialogRef.close(this.isNeedToUpdateHero);
  }

  private updateTabParam(id: string) {
    this.router.navigate([], {
      queryParams: {
        'inventory-tab': `inventory-${this.translateService
          .instant(this.inventoryTypes.find(it => it.id === id)!.label)
          .toLowerCase()}`,
      },
      queryParamsHandling: 'merge',
    });
  }

  init() {
    if (this.account && this.chainId) {
      this.loadHeroInfo();
    }
  }

  private loadHeroInfo() {
    this.subgraphService
      .hero$(this.heroAdr, this.heroId)
      .pipe(
        finalize(() => {}),
        takeUntil(this.destroy$),
      )
      .subscribe(hero => {
        this.logger.trace('hero', hero);
        if (hero) {
          this.hero = hero;

          this.equippedItemBySlots.clear();
          for (const eqItem of this.hero.items) {
            this.equippedItemBySlots.set(eqItem.equippedSlot ?? 0, eqItem);
          }

          this.loadItems();
        }
      });
  }

  private loadItems() {
    forkJoin({
      userItems: this.subgraphService.usersItems$(this.account),
      heroSandbox: this.subgraphService.heroSandboxItems$(`${this.heroAdr}-${this.heroId}`),
    })
      .pipe(
        finalize(() => {
          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ userItems, heroSandbox }) => {
        this.hasSandboxItems = heroSandbox.length > 0;
        const items = [...userItems, ...heroSandbox];
        this.items = items;

        this.getPositionsPerPageWithSortAndFilter();

        this.availableItemsBySlots.clear();
        for (const item of this.items) {
          if (
            item.meta.itemType === ITEM_TYPE.TWO_HAND &&
            (this.equippedItemBySlots.has(ITEM_SLOT.LEFT_HAND) || this.equippedItemBySlots.has(ITEM_SLOT.RIGHT_HAND))
          ) {
            continue;
          }

          if (this.isItemEligibleForHero(item)) {
            const slots = ITEM_TYPE_TO_ITEM_SLOTS_MAPPING.get(item.meta.itemType) ?? [];
            for (const slot of slots) {
              const _items = this.availableItemsBySlots.get(slot) ?? [];
              _items.push(item);
              this.availableItemsBySlots.set(slot, _items);
            }
          }
        }

        this.loading = false;

        this.changeDetectorRef.detectChanges();

        this.mediator.dispatch(new LoadingActions.PageLoaded(MAIN_ROUTES.INVENTORY));
      });
  }

  private resetInventorySlots(): void {
    rangeArray(0, this.itemSlotsArray.length).forEach(i => {
      this.availableItemsBySlots.set(i, []);
    });
  }

  protected readonly VIEW_MODE = VIEW_MODE;
}
